// Everyday-name search (F05). Index is built on first use from the compiled structure index.
import MiniSearch from 'minisearch';
import { STRUCTURES, type StructureEntry } from '../state/structures';

export interface Result { entry: StructureEntry; via: string | null; score: number }

const norm = (s: string) =>
  s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[’']/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

/** UK/AU ⇄ US spelling variants so either spelling finds the card. */
export function spellingVariants(term: string): string[] {
  const out = new Set([term]);
  const pairs: Array<[RegExp, string]> = [[/oe/g, 'e'], [/ae/g, 'e'], [/our\b/g, 'or'], [/re\b/g, 'er']];
  for (const [re, rep] of pairs) if (re.test(term)) out.add(term.replace(re, rep));
  return [...out];
}

let index: MiniSearch<{ id: string; title: string; name: string; everyday: string; synonyms: string; misspellings: string }> | null = null;
let exact: Map<string, Array<{ id: string; via: string; kind: 'everyday' | 'name' }>> | null = null;

function build() {
  const docs = STRUCTURES.filter((s) => s.selectable).map((s) => ({
    id: s.id,
    title: s.title,
    name: s.name,
    everyday: s.aliases.everyday.join(' | '),
    synonyms: [...s.aliases.synonyms, s.id.replace(/-/g, ' ')].join(' | '),
    misspellings: s.aliases.misspellings.join(' | '),
  }));
  index = new MiniSearch({
    fields: ['title', 'name', 'everyday', 'synonyms', 'misspellings'],
    storeFields: ['id'],
    processTerm: (t) => norm(t) || null,
    searchOptions: {
      boost: { everyday: 4, title: 3, name: 3, synonyms: 2, misspellings: 1.5 },
      prefix: true,
      fuzzy: (term) => (term.length >= 5 ? 0.25 : false),
      combineWith: 'AND',
    },
  });
  index.addAll(docs);
  exact = new Map();
  const add = (k: string, v: { id: string; via: string; kind: 'everyday' | 'name' }) => {
    const key = norm(k);
    if (!key) return;
    if (!exact!.has(key)) exact!.set(key, []);
    exact!.get(key)!.push(v);
  };
  for (const s of STRUCTURES.filter((x) => x.selectable)) {
    for (const a of s.aliases.everyday) add(a, { id: s.id, via: a, kind: 'everyday' });
    for (const a of [...s.aliases.synonyms, ...s.aliases.misspellings]) add(a, { id: s.id, via: a, kind: 'name' });
    add(s.title.replace(/^the\s+/i, ''), { id: s.id, via: '', kind: 'name' });
    add(s.name, { id: s.id, via: '', kind: 'name' });
  }
}

const BY = new Map(STRUCTURES.map((s) => [s.id, s]));

export function search(query: string, limit = 8): Result[] {
  if (!index) build();
  const q = norm(query);
  if (!q) return [];
  const out = new Map<string, Result>();
  for (const v of spellingVariants(q)) {
    for (const hit of exact!.get(v) ?? []) {
      const entry = BY.get(hit.id)!;
      const score = (hit.kind === 'everyday' ? 1000 : 900) + (entry.kind === 'full' ? 10 : 0);
      if (!out.has(hit.id) || out.get(hit.id)!.score < score) out.set(hit.id, { entry, via: hit.via && norm(hit.via) !== norm(entry.title) ? hit.via : null, score });
    }
    for (const r of index!.search(v)) {
      const entry = BY.get(r.id as string)!;
      const boost = entry.kind === 'full' ? 1.3 : entry.kind === 'stub' ? 1.1 : 1;
      const viaAlias = entry.aliases.everyday.find((a) => norm(a).includes(v.split(' ')[0]));
      // Prefix of the title ("hear" → "The heart"): prefer the shortest such title.
      const title = norm(entry.title).replace(/^the /, '');
      const prefixBonus = title.startsWith(v) ? r.score * 0.5 + 200 / title.length : 0;
      const score = r.score * boost + prefixBonus;
      if (!out.has(entry.id) || out.get(entry.id)!.score < score) out.set(entry.id, { entry, via: viaAlias && norm(viaAlias) !== norm(entry.title) ? viaAlias : null, score });
    }
  }
  return [...out.values()].sort((a, b) => b.score - a.score).slice(0, limit);
}

/** Up to n starter suggestions for an empty-result query. */
export function suggestions(n = 3): StructureEntry[] {
  const picks = ['heart', 'ulnar-nerve', 'femur', 'brain', 'lungs', 'patella', 'thyroid-cartilage', 'vagus-nerve'];
  return picks.map((id) => BY.get(id)).filter((s): s is StructureEntry => !!s && s.kind !== 'none').slice(0, n);
}

export function surprise(exclude?: string): StructureEntry | null {
  const pool = STRUCTURES.filter((s) => s.selectable && s.kind === 'full' && s.id !== exclude);
  if (!pool.length) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}
